import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template155: Ópera (Vendôme, Ópera Garnier, Lafayette, Champs-Élysées, Arco del Triunfo). Todo desde backend.
// page.texts[0]=header, [1]=intro | page.sections[0..4] (imageId, title, body, sabiasQue[0]; layout box sin imagen para 1 y 3) | page.tipBox (text, imageId) | page.images, page.canvasImages (logo)
const Template155 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const introText = page?.texts?.[1]?.content;
	const logoImage = getCanvasImageUrl("logo");

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const section0 = sections[0];
	const section1 = sections[1];
	const section2 = sections[2];
	const section3 = sections[3];
	const section4 = sections[4];
	const tipBox = page?.tipBox ?? page?.data?.tipBox ?? page?.data?.data?.tipBox;

	const vendomeImage = getImageSrc(section0?.imageId);
	const galeriasImage = getImageSrc(section2?.imageId);
	const arcoImage = getImageSrc(section4?.imageId);
	const calleParisImage = tipBox?.imageId
		? getImageSrc(tipBox.imageId)
		: undefined;

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			<Box data-block-id="text-1" sx={{ padding: "10px 12px", paddingTop: 0, position: "relative" }}>
				{introText && (
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
							marginTop: "10px",
						}}
					>
						{introText}
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "12px",
						marginTop: "10px",
					}}
				>
					{/* Plaza Vendôme */}
					{section0 && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{vendomeImage && (
								<img
									src={vendomeImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							<Box sx={{ width: vendomeImage ? "60%" : "100%" }}>
								{section0.title && (
									<Box component="h3" sx={estiloH3}>
										{section0.title}
									</Box>
								)}
								{section0.body && (
									<Box component="p" sx={estiloP}>
										{section0.body}
									</Box>
								)}
							</Box>
							{section0.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
									<SabiasQue
										icono={section0.sabiasQue[0].icono}
										texto={section0.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Ópera Garnier */}
					{section1 && (
						<Box
							data-block-id="text-3"
							sx={{
								backgroundColor: "#E8F4F8",
								padding: "12px",
								borderRadius: "12px",
								position: "relative",
							}}
						>
							{section1.title && (
								<Box component="h3" sx={estiloH3}>
									{section1.title}
								</Box>
							)}
							{section1.body && (
								<Box component="p" sx={estiloP}>
									{section1.body}
								</Box>
							)}
							{section1.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -12, right: "30%" }}>
									<SabiasQue
										icono={section1.sabiasQue[0].icono}
										texto={section1.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Galeries Lafayette */}
					{section2 && (
						<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{galeriasImage && (
								<img
									src={galeriasImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							<Box sx={{ width: galeriasImage ? "60%" : "100%" }}>
								{section2.title && (
									<Box component="h3" sx={estiloH3}>
										{section2.title}
									</Box>
								)}
								{section2.body && (
									<Box component="p" sx={estiloP}>
										{section2.body}
									</Box>
								)}
							</Box>
							{section2.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
									<SabiasQue
										icono={section2.sabiasQue[0].icono}
										texto={section2.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Tip box (Rue de Rivoli...) */}
					{tipBox?.text && (
						<Box
							data-block-id="text-5"
							sx={{
								backgroundColor: "#E8F4F8",
								border: `3px solid ${actualHeaderColor}`,
								borderRadius: "12px",
								padding: "12px 12px",
								fontSize: 12,
								fontFamily: `${
									fontFamily || "'Poppins', sans-serif"
								} !important`,
								color: "#333",
								position: "relative",
								marginTop: "8px",
							}}
						>
							<Box
								component="p"
								sx={{ ...estiloP, width: isSmallScreen ? "60%" : "80%" }}
							>
								{tipBox.text}
							</Box>
							{calleParisImage && (
								<img
									src={calleParisImage}
									alt=""
									style={{
										width: 100,
										position: "absolute",
										bottom: isSmallScreen ? 0 : 20,
										right: isSmallScreen ? 0 : -34,
									}}
								/>
							)}
						</Box>
					)}

					{/* Champs-Élysées */}
					{section3 && (
						<Box
							data-block-id="text-6"
							sx={{
								backgroundColor: "#FFF5E6",
								padding: "12px",
								borderRadius: "12px",
								position: "relative",
							}}
						>
							{section3.title && (
								<Box component="h3" sx={estiloH3}>
									{section3.title}
								</Box>
							)}
							{section3.body && (
								<Box component="p" sx={estiloP}>
									{section3.body}
								</Box>
							)}
							{section3.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -12, right: "30%" }}>
									<SabiasQue
										icono={section3.sabiasQue[0].icono}
										texto={section3.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Arco del Triunfo */}
					{section4 && (
						<Box data-block-id="text-7" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{arcoImage && (
								<img
									src={arcoImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							<Box sx={{ width: arcoImage ? "60%" : "100%" }}>
								{section4.title && (
									<Box component="h3" sx={estiloH3}>
										{section4.title}
									</Box>
								)}
								{section4.body &&
									(section4.bodyHtml ? (
										<Box
											component="p"
											sx={estiloP}
											dangerouslySetInnerHTML={{ __html: section4.body }}
										/>
									) : (
										<Box component="p" sx={estiloP}>
											{section4.body}
										</Box>
									))}
							</Box>
							{section4.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
									<SabiasQue
										icono={section4.sabiasQue[0].icono}
										texto={section4.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template155.thumbnail = "/images/template-1.png";

export default Template155;
